import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Users, Instagram, Facebook } from "lucide-react";
import { scrollTo } from "./Nav";
import { mediaUrl } from "@/lib/api";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ content }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const heroImage = content?.hero_image || null;

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      <div className="absolute -top-32 -right-40 w-[38rem] h-[38rem] rounded-full bg-[#EFE6DC] blur-3xl opacity-70" />
      <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-[#E9EFE5] blur-3xl opacity-60" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#C85A32]/30 bg-white/70 px-4 py-2 mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C85A32] opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C85A32]" />
            </span>
            <span data-testid="hero-badge" className="text-xs font-medium uppercase tracking-[0.25em] text-[#C85A32]">
              {content?.hero_badge || "Ouverture prochaine à Auch"}
            </span>
          </motion.div>

          <h1 className="font-display font-medium tracking-tight text-[#201815] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            <MaskedLine delay={0.1}>{content?.hero_line1 || "Peignez."}</MaskedLine>
            <MaskedLine delay={0.2}>
              <span className="italic text-[#C85A32]">{content?.hero_line2 || "Dégustez."}</span>
            </MaskedLine>
            <MaskedLine delay={0.3}>{content?.hero_line3 || "Créez."}</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-[#5C4E48] leading-relaxed"
          >
            {content?.hero_subtitle ||
              "Le premier café céramique d'Auch ouvre ses portes. Choisissez une pièce brute, peignez-la autour d'un bon café, et repartez avec une œuvre unique émaillée et cuite par nos soins."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-reserve-btn"
              onClick={() => scrollTo("#reservation")}
              className="group inline-flex items-center gap-3 rounded-full bg-[#201815] text-[#FBF8F3] px-7 py-4 text-sm font-medium hover:bg-[#C85A32] transition-colors duration-300"
            >
              Réserver mon atelier
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-discover-btn"
              onClick={() => scrollTo("#atelier")}
              className="inline-flex items-center gap-2 rounded-full border border-[#201815]/20 px-7 py-4 text-sm font-medium text-[#201815] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors duration-300"
            >
              Découvrir l'atelier 2h15
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#8A7971]"
          >
            <span className="inline-flex items-center gap-2">
              <Users size={15} className="text-[#C85A32]" />
              <span data-testid="hero-capacity" className="font-mono-brand text-xs tracking-wide">14 places max / créneau</span>
            </span>
            <span className="inline-flex items-center gap-2">
              <MapPin size={15} className="text-[#C85A32]" />
              {content?.address || "Auch, Gers"}
            </span>
            {(content?.instagram_url || content?.facebook_url) && (
              <span data-testid="hero-social" className="inline-flex items-center gap-2.5">
                {content?.instagram_url && (
                  <a
                    href={content.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-social-instagram"
                    aria-label="Instagram"
                    className="h-8 w-8 rounded-full border border-[#201815]/15 bg-white/60 flex items-center justify-center text-[#5C4E48] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors duration-300"
                  >
                    <Instagram size={14} />
                  </a>
                )}
                {content?.facebook_url && (
                  <a
                    href={content.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-social-facebook"
                    aria-label="Facebook"
                    className="h-8 w-8 rounded-full border border-[#201815]/15 bg-white/60 flex items-center justify-center text-[#5C4E48] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors duration-300"
                  >
                    <Facebook size={14} />
                  </a>
                )}
              </span>
            )}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] border-[6px] border-white shadow-[0_40px_80px_-30px_rgba(32,24,21,0.35)]">
                {heroImage ? (
                  <img src={mediaUrl(heroImage)}
                    alt="Atelier de peinture sur céramique au café"
                    data-testid="hero-image"
                    loading="eager"
                    fetchPriority="high"
                    className="w-full h-[30rem] sm:h-[34rem] object-cover"
                  />
                ) : (
                  <div data-testid="hero-image" className="w-full h-[30rem] sm:h-[34rem] bg-[#EFE6DC]" />
                )}
              </div>
              <div className="absolute -bottom-8 -left-8 animate-float-soft">
                <div className="relative h-32 w-32">
                  <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow">
                    <defs>
                      <path id="circlePath" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
                    </defs>
                    <circle cx="50" cy="50" r="50" fill="#C85A32" />
                    <text fill="#FBF8F3" fontSize="9.5" letterSpacing="2.5" fontFamily="Outfit, sans-serif">
                      <textPath href="#circlePath">CAFÉ CÉRAMIQUE • AUCH • GERS •</textPath>
                    </text>
                  </svg>
                  <img
                    src={content?.logo_url || "/logo.png"}
                    alt="Logo Terre à Deux"
                    data-testid="hero-stamp-logo"
                    className="absolute inset-0 m-auto h-16 w-16 rounded-full object-cover border-2 border-[#FBF8F3]/50"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
